import { SYSTEM_DEFECTS } from 'config/defects';
import './AnswerDetail.scss';
import Decimal from 'decimal.js';
import { normalizeDateAnswer, parseDateAnswer } from 'lib/dateMeasure';
import { formatDateToHTMLDateString } from 'lib/dateHelpers';
import {
  addDays,
  addMonths,
  addYears,
  differenceInCalendarDays,
} from 'date-fns';

/** True when a measure source can actually answer an expected-value lookup. */
export const hasMeasureData = (source) =>
  !!source?.measureTable?.columnValues?.UOM?.length ||
  !!source?.customMeasurements?.length;

/**
 * Pick the measure source for expected values: the asset snapshot captured at
 * inspection time, falling back to the live asset.
 *
 * The snapshot is preferred so an inspection keeps the measurements it was
 * judged against. A plain `??` is not enough though - a snapshot row can exist
 * while carrying an empty `measureTable`, and an empty object is not
 * nullish, so the expected value and deviation silently disappeared instead of
 * falling back to the asset that does hold the data.
 */
export const resolveMeasureSource = (snapshot, asset) => {
  if (hasMeasureData(snapshot)) return snapshot;
  if (hasMeasureData(asset)) return asset;
  return snapshot ?? asset ?? null;
};

/** IN <-> CM conversion factor. */
const IN_TO_CM = 2.54;

/**
 * Length units the comparison can convert between, keyed by every spelling that
 * reaches here. The asset measure table labels inches `"`, the tablet records
 * `in`, and neither side is consistent about case or plurals.
 */
const LENGTH_UNIT_ALIASES = Object.assign(Object.create(null), {
  'in': 'in',
  'inch': 'in',
  'inches': 'in',
  '"': 'in',
  'cm': 'cm',
  'cms': 'cm',
  'centimeter': 'cm',
  'centimeters': 'cm',
  'centimetre': 'cm',
  'centimetres': 'cm',
});

/**
 * Every unit alias, lengths included. Weights convert nowhere, but two
 * spellings of the same weight still describe the same quantity, so they must
 * not be written off as incomparable.
 */
const UNIT_ALIASES = Object.assign(Object.create(null), LENGTH_UNIT_ALIASES, {
  'lb': 'lb',
  'lbs': 'lb',
  'pound': 'lb',
  'pounds': 'lb',
  'kg': 'kg',
  'kgs': 'kg',
  'kilogram': 'kg',
  'kilograms': 'kg',
  'g': 'g',
  'gram': 'g',
  'grams': 'g',
});

/** Reduce a unit to the form it shares with its other spellings. */
const canonicalUnit = (unit) => {
  const key = String(unit ?? '').trim().toLowerCase();
  return UNIT_ALIASES[key] ?? key;
};

/** Map a unit onto a known length unit, else null. */
export const normalizeLengthUnit = (unit) =>
  LENGTH_UNIT_ALIASES[String(unit ?? '').trim().toLowerCase()] ?? null;

/**
 * Convert a length between inches and centimetres.
 *
 * Returns null when no conversion applies - either unit unknown, both sides
 * already the same unit, or a non-numeric value - which keeps
 * `answerValueConverted` meaning "a conversion actually happened".
 */
export const convertLength = (value, fromUnit, toUnit) => {
  const from = normalizeLengthUnit(fromUnit);
  const to = normalizeLengthUnit(toUnit);

  if (!from || !to || from === to || !Number.isFinite(value)) return null;

  const converted =
    from === 'in'
      ? new Decimal(value).mul(IN_TO_CM)
      : new Decimal(value).div(IN_TO_CM);

  return converted.toDP(2).toNumber();
};

/**
 * Whether the recorded and expected units describe genuinely different things.
 *
 * A missing unit on either side is not a mismatch - plenty of measure tables
 * carry no unit at all and their deviations have always been comparable. Only
 * two units that are both present and irreconcilable count, and those must not
 * produce a deviation: comparing 100in against 100cm as bare numbers reported a
 * perfect match while hiding a defect.
 */
export const hasUnitMismatch = (answerUnit, targetUnit) => {
  const answer = canonicalUnit(answerUnit);
  const target = canonicalUnit(targetUnit);
  return !!answer && !!target && answer !== target;
};

/** Offset a date by an amount of the question's Day/Month/Year unit. */
const offsetMeasureDate = (date, amount, unit) => {
  switch ((unit ?? '').toLowerCase()) {
    case 'year':
      return addYears(date, amount);
    case 'month':
      return addMonths(date, amount);
    default:
      return addDays(date, amount);
  }
};

/**
 * The attributes each `measureSourceType` of a `Date` question reads from: a WO
 * custom field (`wo`, e.g. the GMP / Reeses import columns), a company attribute
 * (`company`) or an asset custom attribute (`custom`). Mirrors the server's
 * date-measure resolution, the legacy `company` fallback included.
 */
const SOURCE_POOLS = {
  wo: (inspection) =>
    (inspection?.woCustomFields ?? []).map((field) => ({
      name: field?.name,
      value: field?.data,
    })),
  custom: (_inspection, asset) => asset?.customFields ?? [],
  company: (_inspection, asset) => [
    ...(asset?.customFields ?? []),
    ...(asset?.companyFields ?? []).map((field) => ({
      name: field?.attribute?.name,
      value: field?.option?.name,
    })),
  ],
};

/** Reference date configured on a `Date` question; null when unresolvable. */
const getDateSourceValue = (question, inspection, asset) => {
  const name = question?.measureSourceName?.trim().toLowerCase();
  const readPool = SOURCE_POOLS[question?.measureSourceType];
  if (!name || !readPool) return null;

  const pairs = readPool(inspection, asset);
  const match = pairs.find(
    (pair) =>
      (pair?.name ?? '').trim().toLowerCase() === name &&
      (pair?.value ?? '') !== '',
  );
  return match ? String(match.value) : null;
};

/**
 * Extracts value and unit from a raw string answer value.
 *
 * @param {String} v Raw answer string
 * @returns answerValue-answerUnit pair
 */
const extractSingleValueAndUnit = (v) => {
  if (v?.length) {
    const vMatches = v.match(/^(-?(\d+\.)?\d+)(.*)$/);
    if (
      vMatches?.length === 4 &&
      vMatches[1]?.length &&
      Number.isFinite(Number(vMatches[1]))
    ) {
      return {
        answerValue: Number(vMatches[1]),
        answerUnit: vMatches[3]?.length ? vMatches[3] : null,
      };
    }
  }

  return null;
};

/**
 * Converts a single raw answer into a list of structured item/s, depending on whether
 * the answer is simple(contains 1 value) or complex(contains multiple dimensions).
 *
 * Multidimensional answers will be split to their dimensions.
 *
 * The output object structure will contain:
 *
 * answerExpectedMeasureTableResult:
 * The UOM row label on the Asset Measure Table where we would look for the target value.
 * For example - Dimensions, Volume, Net Weight, etc.
 * Empty for Custom/Other answers.
 *
 * answerExpectedMeasureTableResultDimension:
 * The Dimension column for complex answers. For example, Dimensions answer has L, W, H
 * separate dimensions. For single-dimension answers, it will be equal to the UOM label.
 *
 * answerValue:
 * Parsed numeric value for the answer.
 *
 * answerUnit:
 * Parsed answer UOM string.
 *
 *
 * @param {String} rawAns Raw answer data from question
 * @param {Object} question
 * @returns Array of answer dimensions in structured form.
 */
const convertRawAnswer = (rawAns, question) => {
  let output = null;

  switch (question.expectedMeasureTableResult) {
    case 'Dimensions': {
      // Dimensions answers are complex - they contain L, W, H dimensions
      // that we first split-out from the answer string, then send one by one
      // to the extractSingleValueAndUnit() to extract each value and unit.
      const dimArray = rawAns.split(', ');
      const dimArrayConverted = dimArray
        ?.map((dim) => {
          if (dim?.length) {
            const dimMatches = dim.match(/^([LWH])=(.*)$/);
            if (dimMatches?.length === 3) {
              return {
                answerExpectedMeasureTableResult:
                  question.expectedMeasureTableResult,
                answerExpectedMeasureTableResultDimension: dimMatches[1],
                ...extractSingleValueAndUnit(dimMatches[2]),
              };
            }
          }
          return null;
        })
        .filter((dim) => dim);
      if (dimArrayConverted?.length) {
        output = dimArrayConverted;
      }
      break;
    }
    case 'Date': {
      // A Date answer is a calendar date, not a number-unit measurement,
      // so it is displayed as-is (normalized to ISO when parseable).
      output = [
        {
          answerExpectedMeasureTableResult: question.expectedMeasureTableResult,
          answerExpectedMeasureTableResultDimension:
            question.expectedMeasureTableResult,
          answerValue: normalizeDateAnswer(rawAns) ?? rawAns,
          answerUnit: null,
        },
      ];
      break;
    }
    case 'Gross W':
    case 'Net W':
    case 'Volume':
    case 'Other':
    case 'Custom': {
      // All other answers for now contain a single dimension, so we just need to
      // extract the value-unit pair.
      output = [
        {
          answerExpectedMeasureTableResult: question.expectedMeasureTableResult,
          answerExpectedMeasureTableResultDimension:
            question.expectedMeasureTableResult,
          ...extractSingleValueAndUnit(rawAns),
        },
      ];
      break;
    }

    default:
      if (
        question.expectedMeasureTableResult == null ||
        question.expectedMeasureTableResult === ''
      ) {
        // Informational quantitative — no target/tolerance comparison set
        // on the question, but the raw answer still needs to be rendered
        // (otherwise "Answer:" shows blank even though actualAnswer is populated).
        output = [
          {
            answerExpectedMeasureTableResult: null,
            answerExpectedMeasureTableResultDimension: null,
            ...(extractSingleValueAndUnit(rawAns) ?? {
              answerValue: rawAns,
              answerUnit: null,
            }),
          },
        ];
      } else {
        console.error(
          'Unknown answer type',
          question.expectedMeasureTableResult,
          question,
        );
      }
  }
  return output;
};

/**
 * A question can hold the actual answer either in .actualAnswer or .singleMeasurements.
 * Depending on where the answer is, we extract it and put it into an array.
 * An answer item itself can be in various formats and can contain multiple dimensions,
 * but here we deal only with creating a unified list of answers.
 *
 * @param {Object} question
 * @returns Array of raw answers
 */
const getAnswerValuesArray = (question) => {
  let output = [];

  if (![null, ''].includes(question?.actualAnswer ?? null)) {
    // If the answer is kept under .actualAnswer, use this as a single-element array.
    output = [question.actualAnswer];
  } else {
    // If the answers are in .singleMeasurements, extract them from there.
    output = question?.singleMeasurements
      ?.map((m) => m.value ?? null)
      ?.filter((m) => ![null, ''].includes(m));
  }

  return output;
};

/**
 * Iterates through raw answers list and generates a list of structured data items.
 * Complex answers will also be split. For exmaple the Dimension answer will generate 3 separate
 * dimension items - L, W, H.
 *
 * @param {Array} rawAnswersList List of raw answer values from question. Can be in various formats
 * @param {Object} question
 * @returns Array of answer items
 */
const convertRawAnswers = (rawAnswersList, question) => {
  const output = [];

  if (rawAnswersList?.length) {
    rawAnswersList.forEach((rawAns, rawAnsIdx) => {
      const convertedAnswerArray = convertRawAnswer(rawAns, question);
      convertedAnswerArray?.forEach((convAns) => {
        output.push({
          answerIndex: rawAnsIdx,
          ...convAns,
        });
      });
    });
  }

  return output;
};

/**
 * Calculates absolute tolerance value from question config and target value.
 *
 * @param {*} v Target value
 * @param {*} dv deltaV tolerance can be either positive/upper(>0) or negative/lower(<0)
 * @param {*} isPercentage Flag whether dV is an absolute value or a percentage
 * @returns Absolute tolerance value.
 */
const getAbsToleranceValue = (v, dv, isPercentage) => {
  const vNum = Number.isFinite(Number(v)) ? Number(v) : null;
  const dvNum = Number.isFinite(Number(dv)) ? Number(dv) : null;

  let output = vNum;

  if (vNum !== null && dvNum !== null && dvNum !== 0) {
    output = isPercentage ? v + (v * dv) / 100 : v + dv;
  }

  return output;
};

/**
 * For a given answerValueItem(answer dimension), generate a structure that contains:
 *
 * targetValue:
 * Reference answer value from question/asset depending on question setup.
 *
 * targetValueUnit:
 * Reference answer value unit.
 *
 * targetValueMin:
 * Calculated absolute lower tolerance value.
 *
 * targetValueMax:
 * Calculated absolute upper tolerance value.
 *
 * @param {Object} question
 * @param {Object} inspection
 * @param {Object} answerValueItem
 * @returns Target value data structure
 */
const getTargetValueData = (question, inspection, answerValueItem, allAnswerValueItems = []) => {
  const output = {
    targetValue: null,
    targetValueUnit: null,
    targetValueMin: null,
    targetValueMax: null,
    expectedUnit: null,
  };

  const inspectionAsset =
    inspection?.inProgressAssetSnapshot ?? inspection?.asset ?? null;

  // Expected measurements fall back to the live asset when the snapshot holds
  // none. Attribute lookups (Date questions) deliberately stay on
  // `inspectionAsset` so they keep reading the historical record.
  const measureSource = resolveMeasureSource(
    inspection?.inProgressAssetSnapshot,
    inspection?.asset,
  );

  if (
    ['Dimensions', 'Gross W', 'Net W', 'Volume'].includes(
      answerValueItem.answerExpectedMeasureTableResult,
    )
  ) {
    const mtUomIdx =
      measureSource?.measureTable?.columnValues?.UOM?.findIndex(
        (_uom) => _uom && _uom === question?.expectedBarcodeUOM,
      );
    if (mtUomIdx > -1) {
      if (answerValueItem.answerExpectedMeasureTableResult === 'Dimensions') {
        output.targetValueUnit =
          measureSource?.measureTable?.columnValues?.Unit?.[mtUomIdx];

        // Get all 3 expected values
        const expectedL = Number(measureSource?.measureTable?.columnValues?.['L']?.[mtUomIdx]) || 0;
        const expectedW = Number(measureSource?.measureTable?.columnValues?.['W']?.[mtUomIdx]) || 0;
        const expectedH = Number(measureSource?.measureTable?.columnValues?.['H']?.[mtUomIdx]) || 0;

        // Sort expected values ascending (keep in original unit)
        const expectedSorted = [expectedL, expectedW, expectedH].sort((a, b) => a - b);

        // Get all actual dimension values from the same answer group
        const dimensionItems = allAnswerValueItems.filter(
          (item) =>
            item.answerExpectedMeasureTableResult === 'Dimensions' &&
            item.answerIndex === answerValueItem.answerIndex,
        );

        // Sort actual values ascending
        const actualSorted = dimensionItems
          .map((item) => item.answerValue)
          .sort((a, b) => a - b);

        // Find the position of current actual value in sorted actual array
        const currentActualValue = answerValueItem.answerValue;
        const sortedIndex = actualSorted.indexOf(currentActualValue);

        // Match with corresponding sorted expected value
        output.targetValue = sortedIndex >= 0 ? expectedSorted[sortedIndex] : expectedSorted[0];

        // Express the recorded answer in the measure table's own unit so the
        // deviation compares like with like. Either direction can need it: the
        // table may be metric and the tablet imperial, or the other way round.
        const converted = convertLength(
          currentActualValue,
          answerValueItem.answerUnit,
          output.targetValueUnit,
        );
        if (converted != null) output.answerValueConverted = converted;
      } else if (
        answerValueItem.answerExpectedMeasureTableResult === 'Gross W'
      ) {
        output.targetValue =
          measureSource?.measureTable?.columnValues?.['Gross W']?.[mtUomIdx];
        output.targetValueUnit =
          measureSource?.measureTable?.columnValues?.['W Unit']?.[mtUomIdx];
      } else if (answerValueItem.answerExpectedMeasureTableResult === 'Net W') {
        output.targetValue =
          measureSource?.measureTable?.columnValues?.['Net W']?.[mtUomIdx];
        output.targetValueUnit =
          measureSource?.measureTable?.columnValues?.['W Unit']?.[mtUomIdx];
      } else if (
        answerValueItem.answerExpectedMeasureTableResult === 'Volume'
      ) {
        output.targetValue =
          measureSource?.measureTable?.columnValues?.['Volume']?.[mtUomIdx];
        output.targetValueUnit =
          measureSource?.measureTable?.columnValues?.['V Unit']?.[mtUomIdx];
      }
    }
  } else if (answerValueItem.answerExpectedMeasureTableResult === 'Other') {
    if (question?.otherMeasure?.length) {
      const cm = measureSource?.customMeasurements?.find(
        (m) => m?.measure === question.otherMeasure,
      );
      output.targetValue = Number.isFinite(Number(cm?.value))
        ? Number(cm.value)
        : null;
      output.targetValueUnit = cm?.unit?.length ? cm?.unit : null;
    }
  } else if (answerValueItem.answerExpectedMeasureTableResult === 'Custom') {
    output.targetValue = Number.isFinite(
      Number(question?.customExpectedMeasureValue),
    )
      ? Number(question.customExpectedMeasureValue)
      : null;
    output.targetValueUnit = question.customExpectedUom?.label?.length
      ? question.customExpectedUom?.label
      : null;
  } else if (answerValueItem.answerExpectedMeasureTableResult === 'Date') {
    // The expected date comes from the configured WO/company attribute; the
    // tolerances span a window around it in the question's Day/Month/Year
    // unit. Kept out of targetValue so the numeric deviation math stays inert.
    const referenceDate = parseDateAnswer(
      getDateSourceValue(question, inspection, inspectionAsset),
    );
    if (referenceDate) {
      output.targetDate = referenceDate;
      output.targetDateMin = Number.isFinite(question?.lowerToleranceValue)
        ? offsetMeasureDate(
            referenceDate,
            -question.lowerToleranceValue,
            question?.otherMeasure,
          )
        : null;
      output.targetDateMax = Number.isFinite(question?.upperToleranceValue)
        ? offsetMeasureDate(
            referenceDate,
            question.upperToleranceValue,
            question?.otherMeasure,
          )
        : null;
    }
  }

  if (Number.isFinite(output.targetValue)) {
    output.targetValueMin = getAbsToleranceValue(
      output.targetValue,
      -question?.lowerToleranceValue,
      question?.lowerToleranceRule !== 'Exact',
    );
    output.targetValueMax = getAbsToleranceValue(
      output.targetValue,
      question?.upperToleranceValue,
      question?.upperToleranceRule !== 'Exact',
    );
  }

  // The expected value's own unit. It must never borrow the recorded answer's
  // when the measure table has one of its own, or a mismatch between the two
  // becomes invisible on screen.
  output.expectedUnit = output.targetValueUnit ?? answerValueItem.answerUnit ?? null;

  return output;
};

/**
 * Calculate answer deviation against the target value and tolerances.
 *
 * The returned structure contains:
 *
 * dAbsolute:
 * Absolute deviation
 *
 * dPercentage:
 * Deviation in percentage relative to the target value
 *
 * diffMsg:
 * Deviation status message, if calculation is successful - defect message.
 *
 * @param {Object} answerValueItem
 * @param {Object} targetValueItem
 * @returns Deviation data structure
 */
const getAnswerDeviation = (answerValueItem, targetValueItem) => {
  const output = {
    dAbsolute: null,
    dPercentage: null,
    diffMsg: null,
    errMsg: null,
    exceedsTolerance: false,
  };

  const { answerUnit, answerValue } = answerValueItem;
  const { targetValue, targetValueMax, targetValueMin, targetValueUnit, answerValueConverted } =
    targetValueItem;

  // Use converted answer value when units were converted (e.g., IN→CM)
  const effectiveAnswerValue = answerValueConverted ?? answerValue;
  const effectiveAnswerUnit = answerValueConverted != null ? targetValueUnit : answerUnit;

  // Validate params and bail out if we cannot calculate the deviation.
  const unitsMismatch = hasUnitMismatch(effectiveAnswerUnit, targetValueUnit);
  if (unitsMismatch) output.errMsg = 'Inconsistent UOM';
  else if (!Number.isFinite(effectiveAnswerValue))
    output.errMsg = 'Missing answer value';
  else if (!Number.isFinite(targetValue))
    output.errMsg = 'Missing target value';

  if (!unitsMismatch && Number.isFinite(effectiveAnswerValue) && Number.isFinite(targetValue)) {
    // Set decimal precision based on what the answer and target value precision is, with a minimum of 2
    const dPrecision = Math.max(
      new Decimal(effectiveAnswerValue).dp(),
      new Decimal(targetValue).dp(),
      2,
    );

    // Calculate absolute deviation
    output.dAbsolute =
      effectiveAnswerValue !== targetValue
        ? new Decimal(effectiveAnswerValue)
          .sub(new Decimal(targetValue))
          .toDP(dPrecision)
          .toNumber()
        : 0;

    // Calculate percentage deviation if we have a non-zero target value.
    if (targetValue !== 0)
      output.dPercentage =
        Number.isFinite(output.dAbsolute) && output.dAbsolute !== 0
          ? new Decimal(output.dAbsolute)
            .div(new Decimal(targetValue))
            .mul(new Decimal(100))
            .toDP(dPrecision)
            .toNumber()
          : 0;

    // Add lower/upper tolerance defect message
    if (Number.isFinite(effectiveAnswerValue)) {
      if (Number.isFinite(targetValueMax) && effectiveAnswerValue > targetValueMax) {
        output.exceedsTolerance = true;
        output.diffMsg = SYSTEM_DEFECTS.UPPER_TOLERANCE;
      } else if (Number.isFinite(effectiveAnswerValue) && effectiveAnswerValue < targetValueMin) {
        output.exceedsTolerance = true;
        output.diffMsg = SYSTEM_DEFECTS.LOWER_TOLERANCE;
      }
    }
  }

  return output;
};

/**
 * Deviation of a recorded date answer from the sourced reference date, in
 * whole days, plus whether it falls outside the tolerance window.
 *
 * @param {Object} answerValueItem
 * @param {Object} targetValueItem
 * @returns Date deviation data structure
 */
const getDateAnswerDeviation = (answerValueItem, targetValueItem) => {
  const output = {
    dDays: null,
    expectedDisplay: null,
    exceedsTolerance: false,
  };

  const answerDate = parseDateAnswer(answerValueItem?.answerValue);
  const { targetDate, targetDateMin, targetDateMax } = targetValueItem ?? {};
  if (!answerDate || !targetDate) return output;

  // Calendar-day comparisons: parseDateAnswer fills the time of day from its
  // reference date, so direct Date comparisons could flip on equal days.
  output.expectedDisplay = formatDateToHTMLDateString(targetDate);
  output.dDays = differenceInCalendarDays(answerDate, targetDate);
  output.exceedsTolerance =
    (targetDateMin != null &&
      differenceInCalendarDays(answerDate, targetDateMin) < 0) ||
    (targetDateMax != null &&
      differenceInCalendarDays(answerDate, targetDateMax) > 0);

  return output;
};

/**
 * - Get actual answer(s) and convert them to an array of { label: index/W/H/L, value, unit }
 * - Get target value(inverse - get target value for actual answer - for example - dimensions call this 3 times)
 * - Get target unit
 * - Get absolute lower and upper tolerance
 * - Get absolute and relative difference between the upper/lower tolerance and the actual answer for each answer
 * - Augment array object above
 * - Render in tabular or single data depending on whether single measurement or multiple(or dimensions).
 */

export const AnswerDetailValuesFunctions = {
  hasMeasureData,
  resolveMeasureSource,
  normalizeLengthUnit,
  convertLength,
  hasUnitMismatch,
  getAnswerValuesArray,
  convertRawAnswers,
  getTargetValueData,
  getAnswerDeviation,
  getDateAnswerDeviation,
};
